import Util from '../../../../forecast-app/shared/util/util';
import {createFragmentContainer, graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {useEffect} from 'react';
import ReportFormatter from "../../../../forecast-app/shared/util/export-formatters/ReportFormatter";
import { hasFeatureFlag } from "../../../../forecast-app/shared/util/FeatureUtil";

export const exportToCsv = (startDate, endDate, resourceData, boolEyeOptions, intl) => {
	const format = new ReportFormatter(intl);

	const new_csv_format = hasFeatureFlag('new_csv_format');

	const formatColumnName = (name) => {
		return new_csv_format ? name.replaceAll(' ', '_').toLowerCase() : name;
	}

	const addColumn = (row, visible, name, value) => {
		if (visible) {
			row[formatColumnName(name)] = value;
		}
	};

	if (resourceData.length > 0) {
		const periodData = resourceData.map(dataRow => {
			const row = {};
			const firstColumnName = dataRow.person ? 'Person' : dataRow.role ? 'Role' : dataRow.department ? 'Department' : '';
			const firstColumnValue = dataRow.person ? `${dataRow.person.firstName} ${dataRow.person.lastName}` : dataRow.role ? dataRow.role.name : dataRow.department ? dataRow.department.name : '';
			// Actuals
			addColumn(row, true, firstColumnName, firstColumnValue);
			addColumn(row, boolEyeOptions.billableProjectHours, 'Actual Billable Project Time', format._formatTime(dataRow.resourceNumbers.actualBillableProjectMinutes));
			addColumn(row, boolEyeOptions.nonBillableProjectHours, 'Actual Non-billable Project Time', format._formatTime(dataRow.resourceNumbers.actualNonBillableProjectMinutes));
			addColumn(row, boolEyeOptions.internalHours, 'Actual Internal Time', format._formatTime(dataRow.resourceNumbers.actualInternalMinutes));
			addColumn(row, boolEyeOptions.totalRegisteredHours, 'Actual Total Time', format._formatTime(dataRow.resourceNumbers.actualTotalMinutes));
			addColumn(row, boolEyeOptions.overHours, 'Actual Over Time', format._formatTime(dataRow.resourceNumbers.actualOverMinutes));
			addColumn(row, boolEyeOptions.actualBillableUtilization, 'Actual Billable Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.actualBillableUtilization));
			addColumn(row, boolEyeOptions.actualNonBillableUtilization, 'Actual Non-billable Utilization',  format._formatPercentageFromFraction(dataRow.resourceNumbers.actualNonBillableUtilization));
			addColumn(row, boolEyeOptions.actualInternalUtilization, 'Actual Internal Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.actualInternalUtilization));
			addColumn(row, boolEyeOptions.actualResourceUtilization, 'Actual Resource Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.actualResourceUtilization));
			// Performance
			addColumn(row, boolEyeOptions.performance, 'Performance', format._formatTime(dataRow.resourceNumbers.performance));
			// Plan
			addColumn(row, boolEyeOptions.billableProjectAllocation, 'Planned Billable Project Time', format._formatTime(dataRow.resourceNumbers.plannedBillableProjectMinutes));
			addColumn(row, boolEyeOptions.nonBillableProjectAllocation, 'Planned Non-billable Project Time', format._formatTime(dataRow.resourceNumbers.plannedNonBillableProjectMinutes));
			addColumn(row, boolEyeOptions.internalAllocation, 'Planned Internal Time', format._formatTime(dataRow.resourceNumbers.plannedInternalMinutes));
			addColumn(row, boolEyeOptions.totalAllocation, 'Planned Total Time', format._formatTime(dataRow.resourceNumbers.plannedTotalMinutes));
			addColumn(row, boolEyeOptions.overAllocation, 'Planned Over Time', format._formatTime(dataRow.resourceNumbers.plannedOverMinutes));
			addColumn(row, boolEyeOptions.plannedBillableUtilization, 'Planned Billable Utilization',  format._formatPercentageFromFraction(dataRow.resourceNumbers.plannedBillableUtilization));
			addColumn(row, boolEyeOptions.plannedNonBillableUtilization, 'Planned Non-billable Utilization',  format._formatPercentageFromFraction(dataRow.resourceNumbers.plannedNonBillableUtilization));
			addColumn(row, boolEyeOptions.plannedInternalUtilization, 'Planned Internal Utilization',  format._formatPercentageFromFraction(dataRow.resourceNumbers.plannedInternalUtilization));
			addColumn(row, boolEyeOptions.plannedResourceUtilization, 'Planned Resource Utilization',  format._formatPercentageFromFraction(dataRow.resourceNumbers.plannedResourceUtilization));
			// Actual vs. Plan
			addColumn(row, boolEyeOptions.billableActualVsPlan, 'Billable - Actual vs Plan', format._formatTime(dataRow.resourceNumbers.billableActualVsPlan));
			addColumn(row, boolEyeOptions.nonBillableActualVsPlan, 'Non-billable - Actual vs Plan', format._formatTime(dataRow.resourceNumbers.nonBillableActualVsPlan));
			addColumn(row, boolEyeOptions.internalActualVsPlan, 'Internal - Actual vs Plan', format._formatTime(dataRow.resourceNumbers.internalActualVsPlan));
			addColumn(row, boolEyeOptions.totalActualVsPlan, 'Total - Actual vs Plan', format._formatTime(dataRow.resourceNumbers.totalActualVsPlan));
			// Remaining
			addColumn(row, boolEyeOptions.billableRemaining, 'Remaining Billable Project Time', format._formatTime(dataRow.resourceNumbers.remainingBillableProjectMinutes));
			addColumn(row, boolEyeOptions.nonBillableRemaining, 'Remaining Non-billable Project Time', format._formatTime(dataRow.resourceNumbers.remainingNonBillableProjectMinutes));
			addColumn(row, boolEyeOptions.internalRemaining, 'Remaining Internal Time', format._formatTime(dataRow.resourceNumbers.remainingInternalMinutes));
			addColumn(row, boolEyeOptions.totalRemaining, 'Remaining Total Time', format._formatTime(dataRow.resourceNumbers.remainingTotalMinutes));
			addColumn(row, boolEyeOptions.overtimeRemaining, 'Remaining Over Time', format._formatTime(dataRow.resourceNumbers.remainingOverMinutes));
			addColumn(row, boolEyeOptions.remainingBillableUtilization, 'Remaining Billable Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.remainingBillableUtilization));
			addColumn(row, boolEyeOptions.remainingNonBillableUtilization, 'Remaining Non-billable Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.remainingNonBillableUtilization));
			addColumn(row, boolEyeOptions.remainingInternalUtilization, 'Remaining Internal Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.remainingInternalUtilization));
			addColumn(row, boolEyeOptions.remainingResourceUtilization, 'Remaining Resource Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.remainingResourceUtilization));
			// Forecast
			addColumn(row, boolEyeOptions.billableForecast, 'Forecasted Billable Project Time', format._formatTime(dataRow.resourceNumbers.forecastBillableProjectMinutes));
			addColumn(row, boolEyeOptions.nonBillableForecast, 'Forecasted Non-billable Project Time', format._formatTime(dataRow.resourceNumbers.forecastNonBillableProjectMinutes));
			addColumn(row, boolEyeOptions.internalForecast, 'Forecasted Internal Time', format._formatTime(dataRow.resourceNumbers.forecastInternalMinutes));
			addColumn(row, boolEyeOptions.totalForecast, 'Forecasted Total Time', format._formatTime(dataRow.resourceNumbers.forecastTotalMinutes));
			addColumn(row, boolEyeOptions.overtimeForecast, 'Forecasted Over Time', format._formatTime(dataRow.resourceNumbers.forecastOverMinutes));
			addColumn(row, boolEyeOptions.forecastBillableUtilization, 'Forecasted Billable Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.forecastBillableUtilization));
			addColumn(row, boolEyeOptions.forecastNonBillableUtilization, 'Forecasted Non-billable Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.forecastNonBillableUtilization));
			addColumn(row, boolEyeOptions.forecastInternalUtilization, 'Forecasted Internal Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.forecastInternalUtilization));
			addColumn(row, boolEyeOptions.forecastResourceUtilization, 'Forecasted Resource Utilization', format._formatPercentageFromFraction(dataRow.resourceNumbers.forecastResourceUtilization));
			// Availability
			addColumn(row, boolEyeOptions.workinghours, 'Working Time', format._formatTime(dataRow.resourceNumbers.workingMinutes));
			addColumn(row, boolEyeOptions.timeOff, 'Time Off', format._formatTime(dataRow.resourceNumbers.timeOffMinutes));
			addColumn(row, boolEyeOptions.availableHours, 'Available Time', format._formatTime(dataRow.resourceNumbers.availableMinutes));

			return row;
		});

		const headerRow = Object.keys(periodData[0]).reduce((map, column) => {
			map[column] = column;
			return map;
		}, {});
		periodData.splice(0, 0, headerRow);

		const csv = Util.JSONToCSV(periodData);
		Util.exportToCSV(csv, `utilization_period_${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.csv`);
	}
};

const PeriodDownload = ({viewer, closeModal, startDate, endDate, boolEyeOptions}) => {
	const intl = useIntl();
	const formatData = viewer => {
		const resourceData = viewer.company.resourceBasedPersons || viewer.company.resourceBasedRoles || viewer.company.resourceBasedDepartments;
		const dataType = viewer.company.resourceBasedPersons ? 'Person' : viewer.company.resourceBasedRoles ? 'Role' : viewer.company.resourceBasedDepartments ? 'Departments' : '';

		return resourceData.edges
			.map(edge => edge.node)
			.map(row => {
				const formattedData = {resourceNumbers: row.resourceNumbers};
				switch (dataType) {
					case 'Person':
						formattedData.person = {
							id: row.id,
							firstName: row.firstName,
							lastName: row.lastName,
							profilePictureId: row.profilePictureId,
						};
						break;
					case 'Role':
						formattedData.role = {
							id: row.id,
							name: row.name,
						};
						break;
					case 'Department':
						formattedData.department = {
							id: row.id,
							name: row.name,
						};
						break;
				}
				return formattedData;
			});
	};

	useEffect(() => {
		if (viewer) {
			exportToCsv(startDate, endDate, formatData(viewer), boolEyeOptions, intl);
			closeModal();
		}
	}, [viewer]);

	return null;
};

export const periodDownloadQuery = graphql`
	query PeriodDownload_Query($startYear: Int!, $startMonth: Int!, $startDay: Int!, $endYear: Int!, $endMonth: Int!, $endDay: Int!, $searchQuery: TaskSearchQueryType!, $pageSize: Int!, $cursor: String) {
		viewer {
			actualPersonId
			component(name: "utilization_report_period_person_csv_loader")
			...PeriodDownload_viewer @arguments(searchQuery: $searchQuery, pageSize: $pageSize, cursor: $cursor, startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay)
		}
	}
`;

export default createFragmentContainer(PeriodDownload, {
	viewer: graphql`
		fragment PeriodDownload_viewer on Viewer
		@argumentDefinitions(
			searchQuery: {type: "TaskSearchQueryType!"}
			startYear: {type: "Int!"}
			startMonth: {type: "Int!"}
			startDay: {type: "Int!"}
			endYear: {type: "Int!"}
			endMonth: {type: "Int!"}
			endDay: {type: "Int!"}
			pageSize: {type: "Int!"}
			cursor: {type: "String"}
		) {
			company {
				resourceBasedPersons(first: $pageSize, after: $cursor, searchQuery: $searchQuery, startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay) {
					edges {
						node {
							id
							firstName
							lastName
							profilePictureId
							resourceNumbers(startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay) {
								# Actuals
								actualBillableProjectMinutes
								actualNonBillableProjectMinutes
								actualInternalMinutes
								actualTotalMinutes
								actualOverMinutes
								actualDoneTaskMinutes
								actualBillableUtilization
								actualNonBillableUtilization
								actualInternalUtilization
								actualResourceUtilization

								# Plan
								plannedBillableProjectMinutes
								plannedNonBillableProjectMinutes
								plannedInternalMinutes
								plannedTotalMinutes
								plannedOverMinutes
								plannedDoneTaskMinutes
								plannedBillableUtilization
								plannedNonBillableUtilization
								plannedInternalUtilization
								plannedResourceUtilization

								# Remaining
								remainingBillableProjectMinutes
								remainingNonBillableProjectMinutes
								remainingInternalMinutes
								remainingTotalMinutes
								remainingOverMinutes
								remainingBillableUtilization
								remainingNonBillableUtilization
								remainingInternalUtilization
								remainingResourceUtilization

								# Forecast
								forecastBillableProjectMinutes
								forecastNonBillableProjectMinutes
								forecastInternalMinutes
								forecastTotalMinutes
								forecastOverMinutes
								forecastBillableUtilization
								forecastNonBillableUtilization
								forecastInternalUtilization
								forecastResourceUtilization

								# Actuals vs. Plan
								billableActualVsPlan
								nonBillableActualVsPlan
								internalActualVsPlan
								totalActualVsPlan

								# Availability
								workingMinutes
								timeOffMinutes
								availableMinutes

								# Performance
								performance
							}
						}
					}
				}
			}
		}
	`,
});
